import { useMemo, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RiEyeLine,
  RiDownloadLine,
  RiSendPlane2Line,
  RiAlarmSnoozeLine,
} from "@remixicon/react";
import { pdf } from "@react-pdf/renderer";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/Drawer";
import TripSheetDocument from "./TripSheetDocument";
import TripSheetPdf from "./TripSheetPdf";
import { getAircraftModelName, getAircraftPhotos } from "@/features/tails/utils";
import { apiClient } from "@/lib/api";
import { formatDateInAirportTimezone, formatTimeInAirportTimezone, getAirportTimezone } from "@/utils/airportTimezone";
import { replaceEmailPlaceholders } from "@/features/quotes/utils/quoteValidation";
import { getDistance } from "geolib";

const TripSheetPreviewDrawer = ({
  isOpen,
  onClose,
  quoteData,
  acceptedBid,
  aircraftDetails,
  organization,
  flights,
  passengers,
  crew,
  services,
  findAirport,
  requestClientPassengers = false,
}) => {
  const navigate = useNavigate();

  // Build initial document data from props
  const initialData = useMemo(() => {
    const clientNameVal = [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");

    const legs = flights.map((flight, idx) => {
      const depAirport = findAirport?.(flight.departure_airport);
      const arrAirport = findAirport?.(flight.arrival_airport);
      const depDate = flight.departure_date ? flight.departure_date : null;

      return {
        legNumber: idx + 1,
        timezone: "",
        flightDistance: "",
        flightDuration: "",
        departure: {
          code: flight.departure_airport || "",
          airportName: depAirport?.name || "",
          address: typeof depAirport?.address === "object" ? depAirport?.address?.street || "" : depAirport?.address || "",
          contactNumber: depAirport?.phone || "",
          weather: "",
          tempHi: "",
          tempLo: "",
          time: depDate ? formatTimeInAirportTimezone(depDate, flight.departure_airport) : "",
          date: depDate ? formatDateInAirportTimezone(depDate, flight.departure_airport, { year: "numeric" }) : "",
          day: depDate ? formatDateInAirportTimezone(depDate, flight.departure_airport, { weekday: "long", day: undefined, month: undefined }) : "",
        },
        arrival: {
          code: flight.arrival_airport || "",
          airportName: arrAirport?.name || "",
          address: typeof arrAirport?.address === "object" ? arrAirport?.address?.street || "" : arrAirport?.address || "",
          contactNumber: arrAirport?.phone || "",
          weather: "",
          tempHi: "",
          tempLo: "",
          time: "",
          date: depDate ? formatDateInAirportTimezone(depDate, flight.arrival_airport, { year: "numeric" }) : "",
          day: depDate ? formatDateInAirportTimezone(depDate, flight.arrival_airport, { weekday: "long", day: undefined, month: undefined }) : "",
        },
        crew: (crew || []).map((c) => ({ name: c.name || "", phone: c.phone || "" })),
        passengers: [...(passengers || [])],
        additionalServices: (services || []).map((s) => ({
          name: s.name,
          serviceDetails: s.details || "",
          address: "",
          phone: "",
          confirmationNumber: "",
        })),
      };
    });

    return {
      operator: {
        name: organization?.name || "",
        address: typeof organization?.address === "object" ? organization?.address?.street || "" : organization?.address || "",
        phone: organization?.phone || "",
        fax: "",
      },
      client: {
        name: clientNameVal,
        company: quoteData?.client?.company_name || "",
        email: quoteData?.client?.email || "",
        phone: quoteData?.client?.phone || "",
      },
      aircraft: {
        image: getAircraftPhotos(aircraftDetails),
        tailNumber: aircraftDetails?.tail_number || "",
        name: getAircraftModelName(aircraftDetails),
        passengerCount: String(acceptedBid?.passenger_count || ""),
      },
      legs,
    };
  }, [quoteData, acceptedBid, aircraftDetails, organization, flights, passengers, crew, services, findAirport]);

  // Local editable state for the document
  const [documentData, setDocumentData] = useState(null);
  const [protectedCounts, setProtectedCounts] = useState(null);
  const [fetchingArrivalTimes, setFetchingArrivalTimes] = useState(false);
  const [fetchingAirportData, setFetchingAirportData] = useState(false);

  // Sync local state when drawer opens or initial data changes, then fetch arrival times
  useEffect(() => {
    if (isOpen && initialData) {
      setDocumentData(JSON.parse(JSON.stringify(initialData)));
      // Store initial counts so pre-populated items can't be removed
      setProtectedCounts({
        legs: initialData.legs.map((leg) => ({
          passengers: leg.passengers.length,
          crew: leg.crew.length,
          services: leg.additionalServices.length,
        })),
      });

      // Fetch arrival times from LLM API for each leg
      if (flights.length) {
        const fetchArrivalTimes = async () => {
          setFetchingArrivalTimes(true);
          const updates = await Promise.allSettled(
            flights.map((flight) => {
              console.log(`${flight.departure_airport} - ${flight.departure_date}`);
              const depTime = formatTimeInAirportTimezone(flight.departure_date, flight.departure_airport, { hour12: true, timeZoneName: 'short' });
              return apiClient.getArrivalTime({
                from_location: flight.departure_airport,
                to_location: flight.arrival_airport,
                departure_time: depTime,
              });
            })
          );

          setDocumentData((prev) => {
            if (!prev) return prev;
            const moment = require('moment-timezone');
            const next = JSON.parse(JSON.stringify(prev));
            updates.forEach((result, idx) => {
              if (result.status === "fulfilled" && result.value?.arrival_time) {
                // Convert "09:16 PM EST" → "21:16" (24-hour format)
                const raw = result.value.arrival_time;
                const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                if (match) {
                  let h = parseInt(match[1], 10);
                  const m = match[2];
                  const period = match[3].toUpperCase();
                  if (period === "PM" && h !== 12) h += 12;
                  if (period === "AM" && h === 12) h = 0;
                  const arrivalTime24h = `${String(h).padStart(2, "0")}:${m}`;
                  next.legs[idx].arrival.time = arrivalTime24h;

                  // Correct arrival date if flight crosses midnight
                  const flight = flights[idx];
                  if (flight?.departure_date) {
                    const arrTz = getAirportTimezone(flight.arrival_airport);
                    // Get departure date as seen in the arrival timezone
                    const depInArrTz = moment.utc(flight.departure_date).tz(arrTz);
                    const arrDateStr = depInArrTz.format('YYYY-MM-DD');
                    // Construct arrival datetime in arrival timezone
                    const arrMoment = moment.tz(`${arrDateStr} ${arrivalTime24h}`, 'YYYY-MM-DD HH:mm', arrTz);
                    // If arrival is before departure (UTC), it must be the next day
                    if (arrMoment.valueOf() < moment.utc(flight.departure_date).valueOf()) {
                      arrMoment.add(1, 'day');
                    }
                    const arrivalDate = arrMoment.toDate();
                    next.legs[idx].arrival.date = formatDateInAirportTimezone(arrivalDate, flight.arrival_airport, { year: "numeric" });
                    next.legs[idx].arrival.day = formatDateInAirportTimezone(arrivalDate, flight.arrival_airport, { weekday: "long", day: undefined, month: undefined });

                    // Compute flight duration from departure UTC to arrival UTC
                    const durationMin = Math.round((arrMoment.valueOf() - moment.utc(flight.departure_date).valueOf()) / 60000);
                    if (durationMin > 0) {
                      const hrs = Math.floor(durationMin / 60);
                      const mins = durationMin % 60;
                      next.legs[idx].flightDuration = `${hrs}h ${String(mins).padStart(2, "0")}m`;
                    }
                  }
                } else {
                  next.legs[idx].arrival.time = raw;
                }
              }
            });
            return next;
          });
          setFetchingArrivalTimes(false);
        };

        fetchArrivalTimes();

        // Fetch timezone and flight distance from airport API for each leg
        const fetchAirportData = async () => {
          setFetchingAirportData(true);
          const results = await Promise.allSettled(
            flights.map(async (flight) => {
              const [dep, arr] = await Promise.all([
                apiClient.getAirportByIcao(flight.departure_airport),
                apiClient.getAirportByIcao(flight.arrival_airport),
              ]);
              return { dep, arr };
            })
          );

          setDocumentData((prev) => {
            if (!prev) return prev;
            const next = JSON.parse(JSON.stringify(prev));
            results.forEach((result, idx) => {
              if (result.status !== "fulfilled") return;
              const { dep, arr } = result.value;
              if (dep?.time_zone) {
                next.legs[idx].timezone = dep.time_zone;
              }
              if (dep?.latitude && dep?.longitude && arr?.latitude && arr?.longitude) {
                const distanceKm = getDistance(
                  { latitude: dep.latitude, longitude: dep.longitude },
                  { latitude: arr.latitude, longitude: arr.longitude },
                ) / 1000;
                const distanceNm = Math.round(distanceKm * 0.539957);
                next.legs[idx].flightDistance = `${distanceNm} NM`;
              }
              // Populate address from airport API data if not already set
              if (dep) {
                if (!next.legs[idx].departure.address) {
                  next.legs[idx].departure.address = [dep.city, dep.state, dep.country].filter(Boolean).join(", ");
                }
                if (!next.legs[idx].departure.airportName && dep.name) {
                  next.legs[idx].departure.airportName = dep.name;
                }
              }
              if (arr) {
                if (!next.legs[idx].arrival.address) {
                  next.legs[idx].arrival.address = [arr.city, arr.state, arr.country].filter(Boolean).join(", ");
                }
                if (!next.legs[idx].arrival.airportName && arr.name) {
                  next.legs[idx].arrival.airportName = arr.name;
                }
              }
            });
            return next;
          });
          setFetchingAirportData(false);
        };

        fetchAirportData();
      }
    }
  }, [isOpen, initialData]);

  // Path-based onChange handler (e.g. "legs.0.crew.1.name")
  const handleChange = useCallback((path, value) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let target = next;
      for (let i = 0; i < keys.length - 1; i++) {
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const handleAddPassenger = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].passengers.push("");
      return next;
    });
  }, []);

  const handleRemovePassenger = useCallback((legIdx, pIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].passengers.splice(pIdx, 1);
      return next;
    });
  }, []);

  const handleAddCrew = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].crew.push({ name: "", phone: "" });
      return next;
    });
  }, []);

  const handleRemoveCrew = useCallback((legIdx, cIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].crew.splice(cIdx, 1);
      return next;
    });
  }, []);

  const handleAddService = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].additionalServices.push({
        name: "",
        serviceDetails: "",
        address: "",
        phone: "",
        confirmationNumber: "",
      });
      return next;
    });
  }, []);

  const handleRemoveService = useCallback((legIdx, sIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].additionalServices.splice(sIdx, 1);
      return next;
    });
  }, []);

  const tripTitle = useMemo(() => {
    if (!flights.length) return "Trip Sheet";
    const dep = flights[0]?.departure_airport || "";
    const arr = flights[0]?.arrival_airport || "";
    return `${dep} - ${arr} Trip`;
  }, [flights]);

  const clientName = useMemo(() => {
    return [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");
  }, [quoteData]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const fetchImageAsBase64 = useCallback(async (src) => {
    let imgSrc = src;
    // Unwrap webpack module objects (e.g. { default: '/static/media/...' })
    if (imgSrc && typeof imgSrc === "object" && imgSrc.default) {
      imgSrc = imgSrc.default;
    }
    if (!imgSrc || typeof imgSrc !== "string" || imgSrc.startsWith("data:")) {
      return imgSrc || null;
    }
    try {
      // For local paths (e.g. /static/media/...), prepend the current origin
      const fetchUrl = imgSrc.startsWith("http") ? imgSrc : window.location.origin + imgSrc;
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Failed to fetch image for PDF:", imgSrc, e);
      return null;
    }
  }, []);

  const resolvePdfData = useCallback(async (data) => {
    const API_BASE = process.env.REACT_APP_API_URL;

    // Resolve aircraft image
    const aircraftImage = await fetchImageAsBase64(data.aircraft.image);

    // Resolve operator logo
    let logoSrc = null;
    if (organization?.branding?.logo) {
      logoSrc = await fetchImageAsBase64(`${API_BASE}${organization.branding.logo}`);
    }

    return {
      ...data,
      aircraft: { ...data.aircraft, image: aircraftImage },
      operatorLogo: logoSrc,
      legs: (data.legs || []).map((leg) => ({
        ...leg,
        passengers: (leg.passengers || []).map((p) =>
          typeof p === "object" ? p.fullName || "" : p
        ),
      })),
    };
  }, [fetchImageAsBase64, organization]);

  const renameWithClientId = (clientId, file) => {
    const client = clientId || 'unknown';
    const newName = `${client}_${file.name}`;
    return new File([file], newName, { type: file.type });
  };

  const handleDownload = useCallback(async () => {
    if (!documentData || isDownloading) return;
    setIsDownloading(true);
    try {
      const pdfData = await resolvePdfData(documentData);
      const blob = await pdf(<TripSheetPdf data={pdfData} brandColor={organization?.branding?.color} />).toBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Trip_Sheet_${documentData.client.name || "Client"}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Failed to generate trip sheet PDF:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [documentData, isDownloading, resolvePdfData]);

  const handleSend = useCallback(async () => {
    if (!documentData || isSending) return;
    const clientEmail = documentData.client.email || quoteData?.client?.email;
    if (!clientEmail) {
       toast.error("No client email address available.");
      return;
    }
    setIsSending(true);
    try {
      const pdfData = await resolvePdfData(documentData);
      const blob = await pdf(<TripSheetPdf data={pdfData} brandColor={organization?.branding?.color} />).toBlob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const clientName = documentData.client.name || "Client";
      await apiClient.sendMessage({
        From: "tools@trustjet.ai",
        To: clientEmail,
        Subject: `Your Trip Sheet – ${tripTitle}`,
        HtmlBody: `<p>Dear ${clientName},</p>
          <p>Please find your trip sheet attached for the upcoming charter flight.</p>
          <p>Best regards,<br>${organization?.name || "TrustJet"}</p>`,
        Attachments: [
          {
            Name: `Trip_Sheet_${clientName}.pdf`,
            Content: base64,
            ContentType: "application/pdf",
          },
        ],
      });

      // Create client passengers batch — use original `passengers` prop for File objects
      // (documentData loses File objects during JSON.parse/stringify deep clone)
      const clientId = quoteData?.client?.client_id || quoteData?.client?.id;
      if (clientId && passengers?.length) {
        const seen = new Set();
        const uniquePassengers = passengers.filter((p) => {
          const key = typeof p === "string" ? p : p.fullName;
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        if (uniquePassengers.length) {
          // Split into existing (already in DB) and new passengers
          const existingPassengers = uniquePassengers.filter((p) => typeof p !== "string" && p.clientPassengerId);
          const newPassengers = uniquePassengers.filter((p) => typeof p === "string" || !p.clientPassengerId);

          // Update existing passengers with any form changes
          if (existingPassengers.length) {
            await Promise.all(
              existingPassengers.map(async (p) => {
                let passport_url = p.existingPassportUrl || null;
                let visa_url = p.existingVisaUrl || null;
                if (p.passportFiles?.length) {
                  const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.passportFiles[0]), "passport");
                  passport_url = uploaded?.file_path || null;
                }
                if (p.visaFiles?.length) {
                  const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.visaFiles[0]), "visa");
                  visa_url = uploaded?.file_path || null;
                }
                let dob = null;
                if (p.dateOfBirth) {
                  const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
                }
                return apiClient.updateClientPassenger(p.clientPassengerId, {
                  full_name: p.fullName || "",
                  date_of_birth: dob,
                  nationality: p.nationality || null,
                  passport_num: null,
                  passport_url,
                  visa_url,
                  weight: p.weight || null,
                  additional_info: {
                    dietary_catering: p.dietaryCatering || null,
                    cabin_environment: p.cabinEnvironment || null,
                    medical_mobility: p.medicalMobility || null,
                    pet_accommodations: p.petAccommodations || null,
                  },
                });
              })
            );
          }

          // Only create new passengers via batch API
          let createdPassengers = [];
          if (newPassengers.length) {
            const newPayload = await Promise.all(
              newPassengers.map(async (p) => {
                if (typeof p === "string") {
                  return { full_name: p };
                }
                let passport_url = p.existingPassportUrl || null;
                let visa_url = p.existingVisaUrl || null;
                if (p.passportFiles?.length) {
                  const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.passportFiles[0]), "passport");
                  passport_url = uploaded?.file_path || null;
                }
                if (p.visaFiles?.length) {
                  const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.visaFiles[0]), "visa");
                  visa_url = uploaded?.file_path || null;
                }
                let dob = null;
                if (p.dateOfBirth) {
                  const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
                }
                return {
                  full_name: p.fullName || "",
                  date_of_birth: dob,
                  nationality: p.nationality || null,
                  passport_num: null,
                  passport_url,
                  visa_url,
                  weight: p.weight || null,
                  additional_info: {
                    dietary_catering: p.dietaryCatering || null,
                    cabin_environment: p.cabinEnvironment || null,
                    medical_mobility: p.medicalMobility || null,
                    pet_accommodations: p.petAccommodations || null,
                  }
                };
              })
            );
            const result = await apiClient.createClientPassengersBatch(clientId, newPayload);
            createdPassengers = Array.isArray(result) ? result : (result?.data || []);
          }

          // Build quote passengers payload from both existing and newly created
          const qId = quoteData?.quote_id || quoteData?.id;
          if (qId) {
            const quotePassengersPayload = [
              // Existing passengers without quotePassengerId — not yet in quote_passengers
              ...existingPassengers.filter((p) => !p.quotePassengerId).map((p) => {
                let dob = null;
                if (p.dateOfBirth) {
                  const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
                }
                return {
                  passenger_name: p.fullName || '',
                  email: null,
                  phone: null,
                  passport_num: null,
                  nationality: p.nationality || null,
                  birth_date: dob,
                  client_passenger_id: p.clientPassengerId,
                };
              }),
              // Newly created passengers — use IDs from batch response
              ...createdPassengers.map((cp, i) => ({
                passenger_name: cp.full_name || newPassengers[i]?.fullName || (typeof newPassengers[i] === "string" ? newPassengers[i] : ''),
                email: null,
                phone: null,
                passport_num: null,
                nationality: cp.nationality || null,
                birth_date: cp.date_of_birth || null,
                client_passenger_id: cp.client_passenger_id || null,
              })),
            ];
            await apiClient.createQuotePassengersBatch(qId, quotePassengersPayload);
          }
        }
      }

      toast.success(`Trip sheet sent to ${clientEmail}`);
      onClose();
      setTimeout(() => navigate("/quotes"), 300);
    } catch (err) {
      console.error("Failed to send trip sheet:", err);
      toast.error("Failed to send trip sheet. Please try again.");
    } finally {
      setIsSending(false);
    }
  }, [documentData, isSending, quoteData, tripTitle, organization, resolvePdfData, onClose, navigate]);


  // send request to client to add passenger details
  const handleRequestSend = async() => {

    const requestToken = await apiClient.createCheckoutToken({
          "quote_id": quoteData.quote_id,
          "amount": 1,
          "currency": "USD",
          "payment_method": "CREDIT_CARD",
          "description": "Charter flight quote payment",
          "metadata": {
              "customer_name": "John Doe",
              "customer_email": "john@example.com"
          }
      });
    
      const requestLink = `${window.location.origin}/tripsheet/${quoteData.quote_id}?token=${requestToken?.token}`;
      const quoteNumber = quoteData.quote_number;
      const clientFirstName = quoteData.client?.clientData?.first_name || quoteData.client?.clientData?.firstName || '';
      const clientLastName = quoteData.client?.clientData?.last_name || quoteData.client?.clientData?.lastName || '';
      const clientName = [clientFirstName, clientLastName].filter(Boolean).join(' ') || 'Valued Client';
      
      const message = `<p>Hi ${clientName}, Please find the below link for adding passengers for quote ${quoteNumber}.</p> <br /> ${requestLink}`;
      const finalEmailMessage = replaceEmailPlaceholders(message, {
        quoteNumber,
        requestLink,
        clientName
      });
    
      // Send message/email to client with the quote
      const messagePayload = {
        From: "tools@trustjet.ai",
        To: quoteData.client?.clientData?.email || quoteData.client?.email,
        Subject: `Passenger Request - (${tripTitle})`,
        HtmlBody: finalEmailMessage,
        Attachments: []
      };

      await apiClient.sendMessage(messagePayload);
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="right" className="sm:max-w-5xl md:max-w-5xl lg:max-w-5xl sm:w-full p-0 bg-gray-50 flex flex-col !gap-0">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-start justify-between py-3 px-5 space-y-0">
          <div>
            <DrawerTitle>{tripTitle}</DrawerTitle>
            <DrawerDescription>Trip Sheet for {clientName || "Client"}</DrawerDescription>
          </div>
          <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors mr-1 !mt-[40px] bg-gray-100 px-1 rounded-md">
            <RiEyeLine className="w-4 h-4" />
            Preview
          </button>
        </DrawerHeader>

        {/* Content - scrollable document preview */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pt-0">
          {documentData && (
            <div style={{ zoom: 0.85 }}>
              <TripSheetDocument
                data={documentData}
                organization={organization}
                isOrgLoading={false}
                onChange={handleChange}
                onAddPassenger={handleAddPassenger}
                onRemovePassenger={handleRemovePassenger}
                onAddCrew={handleAddCrew}
                onRemoveCrew={handleRemoveCrew}
                onAddService={handleAddService}
                onRemoveService={handleRemoveService}
                protectedCounts={protectedCounts}
                readOnlyAircraft
                fetchingArrivalTimes={fetchingArrivalTimes}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-4 bg-white border-t border-gray-200">
          {/* {!requestClientPassengers && (
            <button
              onClick={handleDownload}
              disabled={isDownloading || !documentData}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiDownloadLine className="w-4 h-4" />
              {isDownloading ? "Generating..." : "Download"}
            </button>
          )}
          <button
            onClick={requestClientPassengers ? handleRequestSend : handleSend}
            disabled={isSending || !documentData}
            className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiSendPlane2Line className="w-4 h-4" />
            {isSending
              ? "Sending..."
              : requestClientPassengers
                ? "Save Draft & Send Request to Client"
                : "Send Trip Sheet"}
          </button> */}

          <button
              onClick={handleDownload}
              disabled={isDownloading || !documentData || fetchingArrivalTimes || fetchingAirportData}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-white border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiDownloadLine className="w-4 h-4" />
              {isDownloading
                ? "Generating..."
                : (fetchingArrivalTimes || fetchingAirportData)
                  ? "Loading..."
                  : "Download"}
            </button>

          <button
            onClick={handleSend}
            disabled={isSending || !documentData || fetchingArrivalTimes || fetchingAirportData}
            className="flex-1 flex text-white items-center justify-center gap-1.5 py-3 bg-primary-700 text-sm rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiSendPlane2Line className="w-4 h-4" />
            {isSending
              ? "Sending..."
              : (fetchingArrivalTimes || fetchingAirportData)
                ? "Loading..."
                : "Send Trip Sheet"}
          </button>

        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default TripSheetPreviewDrawer;